import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { attrRicerca } from "@/lib/listaColonne";
import { VEHICLE_CARBURANTE_LABELS } from "@/lib/labels";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { errore, fmtTelefono, SceltaSoggetto, CampoNota } from "./crmComuni";
import { useSediCrm, OpzioniSede, TUTTE } from "./SpostaPipeline";

export const CAMPI_DESCRITTO = [["marca", "Marca"], ["modello", "Modello"], ["versione", "Versione"], ["anno", "Anno", "number"],
  ["km", "Km", "number"], ["targa", "Targa"],
  // Sprint CRM-2A.1 — stesse etichette del veicolo agganciato, uno accanto all'altro
  ["prezzo_vendita", "Prezzo di Vendita", "number"], ["prezzo_web", "Prezzo WEB", "number"], ["telaio", "Telaio"], ["note", "Note"]];

export function VeicoloDescrittoForm({ vd, setVd, prefix }) {
  const set = (k, v) => setVd({ ...vd, [k]: v === "" ? null : v });
  return (
    <div className="grid grid-cols-3 gap-2" data-testid={`${prefix}-descritto`}>
      {CAMPI_DESCRITTO.filter(([k]) => k !== "note").map(([k, l, t]) => (
        <div key={k}><Label className="text-xs">{l}</Label>
          <Input type={t || "text"} value={vd?.[k] ?? ""} onChange={(e) => set(k, t ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
            data-testid={`${prefix}-vd-${k}`} /></div>
      ))}
      <div><Label className="text-xs">Alimentazione</Label>
        <Select value={vd?.alimentazione || "_"} onValueChange={(v) => set("alimentazione", v === "_" ? "" : v)}>
          <SelectTrigger data-testid={`${prefix}-vd-alimentazione`}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="_">—</SelectItem>
            {Object.entries(VEHICLE_CARBURANTE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select></div>
      {/* Sprint CRM-10b — Note su una riga sua, a tutta larghezza; l'altezza segue il testo (fino a ~15 righe, poi scorre) */}
      <div className="col-span-3" data-testid={`${prefix}-vd-note-riga`}><Label className="text-xs">Note</Label>
        <CampoNota value={vd?.note ?? ""} onChange={(v) => set("note", v)} testid={`${prefix}-vd-note`} maxRighe={15} /></div>
    </div>
  );
}

export function CercaVeicolo({ companyId, onScegli, prefix }) {
  const [q, setQ] = useState("");
  const [lista, setLista] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => api.get(`/crm/veicoli-agganciabili?company_id=${companyId}&q=${encodeURIComponent(q)}`)
      .then((r) => setLista(r.data)).catch(() => setLista([])), 250);
    return () => clearTimeout(t);
  }, [q, companyId]);
  return (
    <div className="space-y-1" data-testid={`${prefix}-cerca-veicolo`}>
      <Input {...attrRicerca(`${prefix}-veicolo`)} placeholder="Codice, marca, modello o targa" value={q} onChange={(e) => setQ(e.target.value)}
        data-testid={`${prefix}-cerca-veicolo-input`} />
      <div className="max-h-40 overflow-auto border rounded">
        {lista.map((v) => (
          <button key={v.id} type="button" onClick={() => onScegli(v)} className="block w-full text-left px-2 py-1 text-sm hover:bg-sky-50"
            data-testid={`${prefix}-veicolo-${v.id}`}>{v.codice || "—"} · {v.marca} {v.modello} · {v.targa || "—"}</button>
        ))}
        {!lista.length && <div className="px-2 py-1 text-xs text-slate-400">Nessun veicolo</div>}
      </div>
    </div>
  );
}

function AvvisoDoppioni({ doppioni, onUsa }) {
  if (!doppioni.length) return null;
  return (
    <div className="rounded border border-amber-300 bg-amber-50 p-2 text-sm space-y-1" data-testid="crm-doppioni">
      <div className="flex items-center gap-1 font-medium text-amber-900"><AlertTriangle className="h-4 w-4" /> Recapito già presente</div>
      {/* Sprint CRM-2A — telefono ed email portano a contatti diversi: si sceglie, nessuna unione automatica */}
      {doppioni.length > 1 && <div className="text-amber-900" data-testid="crm-doppioni-diversi">Il telefono e l'email portano a contatti diversi: scegli quale usare.</div>}
      {doppioni.map((d) => (
        <div key={d.id} className="flex items-center justify-between gap-2">
          <span>{d.nome} · {d.recapito} · {d.n_opportunita} opportunità</span>
          <Button size="sm" variant="outline" onClick={() => onUsa(d)} data-testid={`crm-usa-contatto-${d.id}`}>Usa questo contatto</Button>
        </div>
      ))}
    </div>
  );
}

export default function NuovaOpportunita({ pipeline, onChiudi }) {
  const nav = useNavigate();
  const cid = pipeline.company_id;
  const sedi = useSediCrm(cid);
  const [f, setF] = useState({ site_id: "", nome: "", telefono: "", email: "", descrizione: "", fonte_id: "",
    tipo_soggetto: "", comune: "", provincia: "" });
  const [contatto, setContatto] = useState(null);
  const [suggeriti, setSuggeriti] = useState([]);
  const [doppioni, setDoppioni] = useState([]);
  const [fonti, setFonti] = useState([]);
  const [modo, setModo] = useState("nessuno");
  const [veicolo, setVeicolo] = useState(null);
  const [vd, setVd] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const unica = sedi.length === 1 ? sedi[0].id : "";
  useEffect(() => { if (unica) set("site_id", unica); }, [unica]);
  useEffect(() => {
    api.get(`/crm/elenchi?company_id=${cid}&tipo=fonte`).then((r) => setFonti(r.data)).catch(() => setFonti([]));
  }, [cid]);
  useEffect(() => {
    if (contatto || f.nome.trim().length < 2) { setSuggeriti([]); return; }
    const t = setTimeout(() => api.get(`/crm/contatti?company_id=${cid}&q=${encodeURIComponent(f.nome)}&page_size=8`)
      .then((r) => setSuggeriti(r.data.items)).catch(() => setSuggeriti([])), 250);
    return () => clearTimeout(t);
  }, [f.nome, contatto, cid]);
  useEffect(() => {
    if (contatto || (!f.telefono.trim() && !f.email.trim())) { setDoppioni([]); return; }
    const p = new URLSearchParams({ company_id: cid, telefono: f.telefono, email: f.email });
    const t = setTimeout(() => api.get(`/crm/contatti/doppioni?${p}`).then((r) => setDoppioni(r.data)).catch(() => setDoppioni([])), 300);
    return () => clearTimeout(t);
  }, [f.telefono, f.email, contatto, cid]);

  const nomeContatto = contatto?.nome || f.nome;
  const scegliVeicolo = (v) => {
    setVeicolo(v);
    if (!f.descrizione.trim()) set("descrizione", `${nomeContatto} – ${v.marca || ""} ${v.modello || ""}`.trim());
  };
  const salva = async () => {
    setBusy(true);
    try {
      const tutte = f.site_id === TUTTE;
      const body = { pipeline_id: pipeline.id, site_id: tutte ? null : f.site_id || null, tutte_le_sedi: tutte,
        descrizione: f.descrizione, fonte_id: f.fonte_id || null };
      if (contatto) body.contatto_id = contatto.id;
      else body.contatto = { nome: f.nome, telefoni: f.telefono ? [f.telefono] : [], email: f.email ? [f.email] : [],
        tipo_soggetto: f.tipo_soggetto, comune: f.comune, provincia: f.provincia };
      if (modo === "archivio" && veicolo) body.vehicle_id = veicolo.id;
      if (modo === "descritto") body.veicolo_descritto = vd;
      const r = await api.post("/crm/opportunita", body);
      toast.success("Opportunità creata");
      nav(`/crm/opportunita/${r.data.id}`);
    } catch (e) { toast.error(errore(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onChiudi(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="crm-nuova-dialog">
        <DialogHeader><DialogTitle>Nuova opportunità — {pipeline.nome}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {sedi.length > 0 && (
            <div><Label>Sede</Label>
              <Select value={f.site_id} onValueChange={(v) => set("site_id", v)}>
                <SelectTrigger data-testid="crm-nuova-sede"><SelectValue placeholder="Scegli la sede" /></SelectTrigger>
                <SelectContent><OpzioniSede sedi={sedi} /></SelectContent>
              </Select></div>
          )}
          {contatto ? (
            <div className="flex items-center justify-between rounded border p-2 text-sm" data-testid="crm-nuova-contatto-scelto">
              <span>Contatto esistente: <b>{contatto.nome}</b></span>
              <Button size="sm" variant="ghost" onClick={() => setContatto(null)} data-testid="crm-nuova-contatto-annulla">Cambia</Button>
            </div>
          ) : (
            <>
              <div><Label>Nome del contatto</Label>
                <Input {...attrRicerca("crm-nome")} value={f.nome} onChange={(e) => set("nome", e.target.value)} data-testid="crm-nuova-nome" />
                {!!suggeriti.length && (
                  <div className="border rounded mt-1 max-h-32 overflow-auto" data-testid="crm-nuova-suggeriti">
                    {suggeriti.map((c) => <button key={c.id} type="button" className="block w-full text-left px-2 py-1 text-sm hover:bg-sky-50"
                      onClick={() => setContatto(c)} data-testid={`crm-suggerito-${c.id}`}>{c.nome} · {[...(c.telefoni || []).map(fmtTelefono), ...(c.email || [])].join(", ")}</button>)}
                  </div>
                )}</div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Telefono</Label><Input value={f.telefono} onChange={(e) => set("telefono", e.target.value)} data-testid="crm-nuova-telefono" /></div>
                <div><Label>Email</Label><Input value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="crm-nuova-email" /></div>
              </div>
              <AvvisoDoppioni doppioni={doppioni} onUsa={setContatto} />
              <div className="grid grid-cols-3 gap-2 items-end">
                <div><Label>Privato o azienda</Label><SceltaSoggetto value={f.tipo_soggetto} onChange={(v) => set("tipo_soggetto", v)} prefix="crm-nuova-soggetto" /></div>
                <div><Label>Comune</Label><Input value={f.comune} onChange={(e) => set("comune", e.target.value)} data-testid="crm-nuova-comune" /></div>
                <div><Label>Provincia</Label><Input value={f.provincia} maxLength={2} onChange={(e) => set("provincia", e.target.value.toUpperCase())} data-testid="crm-nuova-provincia" /></div>
              </div>
            </>
          )}
          <div><Label>Veicolo</Label>
            <Select value={modo} onValueChange={setModo}>
              <SelectTrigger data-testid="crm-nuova-modo-veicolo"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="nessuno">Nessun veicolo</SelectItem><SelectItem value="archivio">Dall'archivio</SelectItem>
                <SelectItem value="descritto">Descritto</SelectItem></SelectContent>
            </Select></div>
          {modo === "archivio" && (veicolo
            ? <div className="text-sm rounded border p-2 flex justify-between" data-testid="crm-nuova-veicolo-scelto">{veicolo.codice} · {veicolo.marca} {veicolo.modello}
              <Button size="sm" variant="ghost" onClick={() => setVeicolo(null)}>Cambia</Button></div>
            : <CercaVeicolo companyId={cid} onScegli={scegliVeicolo} prefix="crm-nuova" />)}
          {modo === "descritto" && <VeicoloDescrittoForm vd={vd} setVd={setVd} prefix="crm-nuova" />}
          <div><Label>Descrizione</Label><Input value={f.descrizione} onChange={(e) => set("descrizione", e.target.value)} data-testid="crm-nuova-descrizione" /></div>
          <div><Label>Fonte</Label>
            <Select value={f.fonte_id || "_"} onValueChange={(v) => set("fonte_id", v === "_" ? "" : v)}>
              <SelectTrigger data-testid="crm-nuova-fonte"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="_">—</SelectItem>{fonti.map((x) => <SelectItem key={x.id} value={x.id}>{x.nome}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onChiudi}>Annulla</Button>
          <Button onClick={salva} disabled={busy} className="bg-[#0F4C81] hover:bg-[#0c3d68]" data-testid="crm-nuova-salva">Crea opportunità</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
